import type {
  IanaTimezoneString,
  ApiISODateString,
  ApiISOTimeString,
} from '#/api/types/ApiTypedDate';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { shorterIsoDateString } from '#/utils/string/date/isoDateString';
import { shortIanaTimezone } from '#/utils/string/date/ianaTimezone';
import { humanIsoTimeString } from '#/utils/string/date/isoTimeString';

type Args = {
  locale: I18nLocale;
  startDate: ApiISODateString;
  startTime: ApiISOTimeString | null;
  endDate: ApiISODateString | null;
  endTime: ApiISOTimeString | null;
  timezone: IanaTimezoneString | null;
};

/**
 * @example
 *
 * //=> Just one date, no time
 * 1/12/2024
 *
 * //=> Two dates, no time
 * 1/12/2024 - 4/02/2025
 *
 * //=> One date, single time
 * 1/12/2024 10:00 AM EDT
 *
 * //=> One date, time range
 * 1/12/2024 10:00 AM - 5:00 PM EDT
 *
 * //=> Two dates with time
 * 1/12/2024 10:00 AM - 4/02/2025 5:00 PM EDT
 */
export function formatSearchHitDate({
  locale,
  startDate,
  startTime,
  endDate,
  endTime,
  timezone,
}: Args) {
  const startString = [
    shorterIsoDateString(startDate, { locale, yearFormat: 'long' }),
    startTime && humanIsoTimeString(startTime, { locale }),
  ]
    .filter(Boolean)
    .join(' ');

  const endString = [
    endDate &&
      endDate !== startDate &&
      shorterIsoDateString(endDate, { locale, yearFormat: 'long' }),
    endTime && humanIsoTimeString(endTime, { locale }),
  ]
    .filter(Boolean)
    .join(' ');

  const combinedStartEndStrnig = [startString, endString]
    .filter(Boolean)
    .join(' - ');

  const timezoneString =
    (startTime || endTime) &&
    timezone &&
    shortIanaTimezone(timezone, { date: startDate, locale });

  return [combinedStartEndStrnig, timezoneString].filter(Boolean).join(' ');
}
